import { initializeUpdates } from './pwa-updates.ts'

type InstallPromptEvent = Event & {
  prompt: () => Promise<unknown>
  userChoice?: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

interface PwaControlElements {
  installButton: HTMLButtonElement
  installDialog: HTMLDialogElement
  closeSettings: () => void
  refreshSettingsAvailability: () => void
}

type InstallPathState = 'available' | 'ios' | 'installed' | 'unavailable' | 'dismissed'

declare global {
  interface Window {
    __pwaTest?: { installPrompts: number }
  }
  interface Navigator {
    standalone?: boolean
  }
}

function installPathElements(elements: PwaControlElements) {
  return {
    title: requiredChild<HTMLHeadingElement>(elements.installDialog, '#install-title'),
    message: requiredChild<HTMLParagraphElement>(elements.installDialog, '#install-message'),
    action: requiredChild<HTMLButtonElement>(elements.installDialog, '#install-dialog-action'),
  }
}

function showInstallPath(elements: PwaControlElements, state: InstallPathState): void {
  const dialog = installPathElements(elements)
  const content: Record<InstallPathState, { title: string; message: string; action: string }> = {
    available: {
      title: 'Install Meet Halfway',
      message: 'Install Meet Halfway for quick launcher access and an app-like window. Planning still requires an internet connection.',
      action: 'Install app',
    },
    ios: {
      title: 'Add Meet Halfway to your Home Screen',
      message: 'On iPhone or iPad, open Share, then choose Add to Home Screen. Planning still requires an internet connection.',
      action: 'Close',
    },
    installed: {
      title: 'Meet Halfway is installed',
      message: 'You are already using Meet Halfway as an installed app.',
      action: 'Continue',
    },
    unavailable: {
      title: 'Install Meet Halfway',
      message: 'An install option is not available in this browser right now. You can keep using Meet Halfway here.',
      action: 'Continue',
    },
    dismissed: {
      title: 'Installation dismissed',
      message: 'Meet Halfway was not installed. You can keep using it in this browser.',
      action: 'Continue',
    },
  }
  dialog.title.textContent = content[state].title
  dialog.message.textContent = content[state].message
  dialog.action.textContent = content[state].action
  dialog.action.dataset.installAction = state === 'available' ? 'prompt' : 'close'
  if (!elements.installDialog.open) elements.installDialog.showModal()
  dialog.action.focus()
}

export function initializePwaControls(elements: PwaControlElements): void {
  const parameters = new URLSearchParams(location.search)
  const scenario = import.meta.env.MODE === 'e2e' ? parameters.get('pwa-test') : null
  const standalone = () => scenario === 'standalone'
    || matchMedia('(display-mode: standalone)').matches
    || matchMedia('(display-mode: minimal-ui)').matches
    || navigator.standalone === true
  const isIos = scenario === 'ios' || /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1)
  const installIntent = parameters.get('intent') === 'install'
  const dialogAction = installPathElements(elements).action
  const installStatus = requiredChild<HTMLElement>(document, '#install-status')
  let installPathActive = installIntent
  let installPrompt: InstallPromptEvent | null = null
  elements.installDialog.addEventListener('close', () => { installPathActive = false })

  if (import.meta.env.PROD && import.meta.env.MODE !== 'e2e' && 'serviceWorker' in navigator) {
    const registration = Promise.resolve().then(() => navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }))
    initializeUpdates({
      button: requiredChild<HTMLButtonElement>(document, '#check-update'),
      status: requiredChild<HTMLElement>(document, '#update-status'),
      dialog: requiredChild<HTMLDialogElement>(document, '#update-dialog'),
    }, registration)
  }

  if (installIntent) showInstallPath(elements, standalone() ? 'installed' : isIos ? 'ios' : 'unavailable')
  if (isIos && !standalone()) elements.installButton.hidden = false
  elements.refreshSettingsAvailability()

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    if (standalone()) return
    installPrompt = event as InstallPromptEvent
    installStatus.hidden = true
    elements.installButton.disabled = false
    elements.installButton.textContent = 'Install app'
    elements.installButton.hidden = false
    elements.refreshSettingsAvailability()
    if (installPathActive) showInstallPath(elements, 'available')
  })

  const requestInstall = async () => {
    elements.closeSettings()
    if (!installPrompt) {
      showInstallPath(elements, standalone() ? 'installed' : isIos ? 'ios' : 'unavailable')
      return
    }
    const prompt = installPrompt
    installPrompt = null
    elements.installButton.hidden = true
    elements.refreshSettingsAvailability()
    const requestedFromInstallPath = elements.installDialog.open
    if (elements.installDialog.open) elements.installDialog.close()
    await prompt.prompt()
    const choice = await prompt.userChoice
    if (choice?.outcome === 'dismissed') {
      installStatus.textContent = 'Installation dismissed. You can keep using this browser.'
      installStatus.hidden = false
      elements.refreshSettingsAvailability()
      if (requestedFromInstallPath) {
        installPathActive = true
        showInstallPath(elements, 'dismissed')
      }
    }
  }

  elements.installButton.addEventListener('click', () => void requestInstall())
  dialogAction.addEventListener('click', () => {
    if (dialogAction.dataset.installAction === 'prompt') void requestInstall()
    else elements.installDialog.close()
  })
  window.addEventListener('appinstalled', () => {
    installPrompt = null
    installStatus.hidden = true
    elements.installButton.hidden = true
    elements.refreshSettingsAvailability()
    if (installPathActive) showInstallPath(elements, 'installed')
  })

  if (import.meta.env.MODE === 'e2e') {
    window.__pwaTest = { installPrompts: 0 }
    if (scenario === 'installable' || scenario === 'install-dismissed') {
      const event = new Event('beforeinstallprompt', { cancelable: true })
      Object.assign(event, {
        prompt: async () => { window.__pwaTest!.installPrompts += 1 },
        userChoice: Promise.resolve({ outcome: scenario === 'install-dismissed' ? 'dismissed' : 'accepted' }),
      })
      window.dispatchEvent(event)
    }
  }
}

function requiredChild<T>(parent: { querySelector(selector: string): Element | null }, selector: string): T {
  const element = parent.querySelector(selector)
  if (!element) throw new Error(`Missing element: ${selector}`)
  return element as T
}
